import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  LinearProgress,
  Typography,
  Tooltip,
} from '@mui/material';
import api from '../../services/api';
import { API_ENDPOINTS } from '../../config/api';

const columns = [
  { id: 'callsign', label: 'CallSign', minWidth: 80 },
  { id: 'cog', label: 'COG', minWidth: 60 },
  { id: 'flag', label: 'Flag', minWidth: 80 },
  { id: 'imo', label: 'IMO', minWidth: 80 },
  { id: 'mmsi', label: 'MMSI', minWidth: 100 },
  { id: 'name', label: 'Name', minWidth: 120 },
  { id: 'nav_description', label: 'Nav Desc.', minWidth: 120 },
  { id: 'nav_status', label: 'Nav Stat.', minWidth: 80 },
  { id: 'sog', label: 'SOG', minWidth: 60 },
  { id: 'type', label: 'Type', minWidth: 80 },
  { id: 'createdAt', label: 'Created At', minWidth: 130 },
  { id: 'updatedAt', label: 'Updated At', minWidth: 130, sortable: true },
];

export default function VesselLists() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [vessels, setVessels] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sortDirection, setSortDirection] = useState('desc');

  const fetchVessels = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(API_ENDPOINTS.VESSEL.GET_ALL, {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          sort: `{"updatedAt": ${sortDirection === 'desc' ? -1 : 1}}`,
        },
      });
      
      if (response.data) {
        setVessels(response.data.vessels || []);
        setTotalCount(response.data.totalItems || 0);
      }
    } catch (error) {
      console.error('Error fetching vessels:', error);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, sortDirection]);

  useEffect(() => {
    fetchVessels();
  }, [fetchVessels]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleSort = () => {
    setSortDirection(prev => prev === 'desc' ? 'asc' : 'desc');
  };

  return (
    <Box sx={{ width: '100%', pl: 1, pr: 2, pt: 0, backgroundColor: 'background.paper' }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
        Vessel Lists
      </Typography>
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 250px)' }}>
          <Table stickyHeader size="small" aria-label="vessel table">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    sx={{ 
                      fontSize: { xs: '0.65rem', md: '0.75rem', lg: '0.8rem' }, 
                      whiteSpace: 'nowrap', 
                      p: { xs: 0.5, md: 1 },
                      cursor: column.sortable ? 'pointer' : 'default',
                      '&:hover': column.sortable ? { 
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        '& .sort-icon': {
                          opacity: 1,
                          transition: 'opacity 0.3s ease',
                        }
                      } : {},
                    }}
                    onClick={column.id === 'updatedAt' ? handleSort : undefined}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {column.label}
                      {column.id === 'updatedAt' && (
                        <Box 
                          className="sort-icon"
                          sx={{ 
                            opacity: 0, 
                            transition: 'opacity 0.3s ease',
                            alignItems: 'center',
                            color: 'primary.main'
                          }}
                        >
                          {sortDirection === 'desc' ? '↓' : '↑'}
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={columns.length} sx={{ p: 0 }}>
                    <LinearProgress color="primary" />
                  </TableCell>
                </TableRow>
              )}
              {vessels.map((vessel) => (
                <TableRow hover tabIndex={-1} key={vessel.id || vessel.MMSI}>
                  <TableCell sx={{ fontSize: { xs: '0.65rem', md: '0.75rem', lg: '0.8rem' }, p: { xs: 0.5, md: 1 } }}>{vessel.CallSign || ""}</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.65rem', md: '0.75rem', lg: '0.8rem' }, p: { xs: 0.5, md: 1 } }}>{vessel.Cog}°</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.65rem', md: '0.75rem', lg: '0.8rem' }, p: { xs: 0.5, md: 1 } }}>{vessel.Country || ""}</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.65rem', md: '0.75rem', lg: '0.8rem' }, p: { xs: 0.5, md: 1 } }}>{vessel.ImoNumber || ""}</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.65rem', md: '0.75rem', lg: '0.8rem' }, p: { xs: 0.5, md: 1 } }}>{vessel.MMSI || ""}</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.65rem', md: '0.75rem', lg: '0.8rem' }, p: { xs: 0.5, md: 1 } }}>
                    <Tooltip title={vessel.Name || ''}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block', maxWidth: 120 }}>{vessel.Name}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.65rem', md: '0.75rem', lg: '0.8rem' }, p: { xs: 0.5, md: 1 } }}>
                    <Tooltip title={vessel.NavigationStatusText || ''}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block', maxWidth: 120 }}>{vessel.NavigationStatusText}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.65rem', md: '0.75rem', lg: '0.8rem' }, p: { xs: 0.5, md: 1 } }}>{vessel.NavigationalStatus || ""}</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.65rem', md: '0.75rem', lg: '0.8rem' }, p: { xs: 0.5, md: 1 } }}>{vessel.Sog}°</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.65rem', md: '0.75rem', lg: '0.8rem' }, p: { xs: 0.5, md: 1 } }}>{vessel.ShipType || ""}</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.65rem', md: '0.75rem', lg: '0.8rem' }, p: { xs: 0.5, md: 1 } }}>
                    {vessel.createdAt 
                      ? new Date(vessel.createdAt).toLocaleString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: false
                        })
                      : ""}
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.65rem', md: '0.75rem', lg: '0.8rem' }, p: { xs: 0.5, md: 1 } }}>
                    {vessel.updatedAt 
                      ? new Date(vessel.updatedAt).toLocaleString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: false
                        })
                      : ""}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ 
            fontSize: { xs: '0.65rem', md: '0.75rem', lg: '0.8rem' },
              '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                fontSize: { xs: '0.65rem', md: '0.75rem', lg: '0.8rem' },
              },
           }}
        />
      </Paper>
    </Box>
  );
}
